import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  formatCompanyDate,
  formatCompanyDateTime,
  formatCompanyTime,
  parseCompanyDate,
  parseCompanyDateTime,
  parseCompanyTime,
  resolveCompanyDisplayFormats
} from '@nova/core';

import {
  companyForDisplay,
  isCompanyDisplayFormatEnabled
} from '@/modules/company/companyDisplayFormatFlag';
import { resolveCompanyDisplaySettings } from '@/modules/company/companyDisplaySettings';
import {
  useCompanySettings,
  type GatedCompanyDisplayFormats
} from '@/modules/company/composables/useCompanySettings';

import type { CarrierCompany } from '@/modules/company/types';

type CompanyArg = Pick<CarrierCompany, 'settings'> | null | undefined;
type CompanyInput = MaybeRefOrGetter<CompanyArg>;

/**
 * Whether company time display is 24-hour (military), soft-gated like format helpers.
 * Name kept for monorepo consistency with Helios/Comet/Satellite (`isMilitaryTimeEnabled`).
 * Safe for Options API mixins that cannot call composables.
 * When the soft gate is off, always false — use warehouse `$isMilitaryTimeEnabled` instead.
 */
export function isCompanyMilitaryTimeEnabled(company?: CompanyArg): boolean {
  if (!isCompanyDisplayFormatEnabled()) {
    return false;
  }
  return (
    resolveCompanyDisplayFormats(companyForDisplay(company)).timeDisplayFormat === 'twentyFourHour'
  );
}

/**
 * Formats and parses dates/times using carrier company display preferences.
 * Soft-gated by enable-luna-company-display-format.
 * Returns one namespaced API object — bind as `companyDisplayFormat` from setup;
 * do not spread (avoids prop shadowing and unused bindings).
 * Omit `company` to use `Auth/me.company` (via useCompanySettings). Pass an explicit
 * value/getter (including `null`) to override.
 */
export function useCompanyDisplayFormat(company?: CompanyInput) {
  const { locale } = useI18n();
  const { gatedCompany, formatPatterns } = useCompanySettings(company);

  const displaySettings = computed(() => resolveCompanyDisplaySettings(gatedCompany.value));

  function resolveCompany(companyOverride?: CompanyArg) {
    if (companyOverride !== undefined) {
      return companyForDisplay(companyOverride);
    }
    if (company !== undefined) {
      return companyForDisplay(toValue(company));
    }
    return gatedCompany.value;
  }

  function buildOptions(timezone?: string, companyOverride?: CompanyArg) {
    return {
      company: resolveCompany(companyOverride) ?? undefined,
      timezone,
      locale: toValue(locale)
    };
  }

  function displayCompany(companyOverride?: CompanyArg) {
    return resolveCompany(companyOverride);
  }

  function companyFormatPatterns(companyOverride?: CompanyArg): GatedCompanyDisplayFormats {
    if (!isCompanyDisplayFormatEnabled()) {
      return {
        ...resolveCompanyDisplayFormats(null),
        dateFormatPattern: undefined,
        timeFormatPattern: undefined,
        dateTimeFormatPattern: undefined
      };
    }
    return resolveCompanyDisplayFormats(resolveCompany(companyOverride));
  }

  function isMilitaryTimeEnabled(companyOverride?: CompanyArg) {
    if (companyOverride !== undefined) {
      return isCompanyMilitaryTimeEnabled(companyOverride);
    }
    if (company !== undefined) {
      return isCompanyMilitaryTimeEnabled(toValue(company));
    }
    return isCompanyMilitaryTimeEnabled(gatedCompany.value);
  }

  function formatDate(
    input: Parameters<typeof formatCompanyDate>[0],
    timezone?: string,
    companyOverride?: CompanyArg
  ) {
    return formatCompanyDate(input, buildOptions(timezone, companyOverride));
  }

  function formatTime(
    input: Parameters<typeof formatCompanyTime>[0],
    timezone?: string,
    companyOverride?: CompanyArg
  ) {
    return formatCompanyTime(input, buildOptions(timezone, companyOverride));
  }

  function formatDateTime(
    input: Parameters<typeof formatCompanyDateTime>[0],
    timezone?: string,
    companyOverride?: CompanyArg
  ) {
    return formatCompanyDateTime(input, buildOptions(timezone, companyOverride));
  }

  function parseDate(value: string, timezone?: string, companyOverride?: CompanyArg) {
    return parseCompanyDate(value, buildOptions(timezone, companyOverride));
  }

  function parseTime(value: string, timezone?: string, companyOverride?: CompanyArg) {
    return parseCompanyTime(value, buildOptions(timezone, companyOverride));
  }

  function parseDateTime(value: string, timezone?: string, companyOverride?: CompanyArg) {
    return parseCompanyDateTime(value, buildOptions(timezone, companyOverride));
  }

  /**
   * Options for custom-field Timestamp formatting.
   * Gate on → company dateTimeFormatPattern (military baked into pattern).
   * Gate off → no pattern so getCustomFieldFormattedValue uses chronon enums + formatAsMilitary.
   */
  function companyTimestampFieldFormatOptions(
    timezone: string,
    companyOverride?: CompanyArg,
    formatAsMilitary = false
  ) {
    if (!isCompanyDisplayFormatEnabled()) {
      return {
        timezone,
        formatAsMilitary,
        locale: toValue(locale)
      };
    }
    const { dateTimeFormatPattern: pattern } = companyFormatPatterns(companyOverride);
    return {
      timezone,
      formatAsMilitary: false,
      dateTimeFormatPattern: pattern,
      locale: toValue(locale)
    };
  }

  return {
    gatedCompany,
    displaySettings,
    formatPatterns,
    displayCompany,
    companyFormatPatterns,
    isMilitaryTimeEnabled,
    formatCompanyDate: formatDate,
    formatCompanyTime: formatTime,
    formatCompanyDateTime: formatDateTime,
    parseCompanyDate: parseDate,
    parseCompanyTime: parseTime,
    parseCompanyDateTime: parseDateTime,
    companyTimestampFieldFormatOptions
  };
}

export type CompanyDisplayFormatApi = ReturnType<typeof useCompanyDisplayFormat>;

/**
 * Company display format helpers for the logged-in carrier (`Auth/me.company`).
 * Bind as `companyDisplayFormat` from setup — do not spread.
 */
export function useLoggedInCompanyDisplayFormat(): CompanyDisplayFormatApi {
  return useCompanyDisplayFormat();
}
